/**
 * La ATENCIÓN: una cita y todo lo que le pertenece.
 *
 * Antes el triaje y las derivaciones eran cajas sueltas en la ficha del
 * paciente, sin decir a qué cita pertenecían: alguien con cinco atenciones
 * tenía un triaje flotando sin dueño. La unidad de trabajo es la atención, y
 * aquí dentro está todo lo suyo: triaje, pago, consulta y derivación.
 */
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  ArrowLeft,
  CalendarPlus,
  FileSignature,
  Lock,
  Stethoscope,
  Wallet,
} from "lucide-react";
import { getCita, listCitas } from "../api/citas";
import { listTriajesDePaciente } from "../api/triaje";
import { listAsignacionesDePaciente } from "../api/asignaciones";
import { listPlanes } from "../api/planes";
import { useAuth } from "../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../auth/TenantBrandingProvider";
import { fechaHora } from "../lib/fecha";
import { Alert, Badge, Button, Card, CardBody, CardHeader } from "../components/ui";
import { EtiquetaOrigen } from "../components/EtiquetaOrigen";
import { TriajeDialog } from "../components/atencion/TriajeDialog";
import { TriajeResumen } from "../components/atencion/TriajeResumen";
import { FlujoAtencionDeCita } from "../components/atencion/FlujoAtencion";
import { DerivarDialog } from "../components/atencion/DerivarDialog";
import { AgendarCitaDialog } from "../components/atencion/AgendarCitaDialog";

const ESTADO: Record<string, { label: string; variant: "neutral" | "success" | "warning" | "danger" }> = {
  PROGRAMADA: { label: "Programada", variant: "neutral" },
  CONFIRMADA: { label: "Confirmada", variant: "success" },
  EN_ATENCION: { label: "En atención", variant: "warning" },
  COMPLETADA: { label: "Atendida", variant: "success" },
  CANCELADA: { label: "Cancelada", variant: "danger" },
  NO_ASISTIO: { label: "No asistió", variant: "danger" },
};

export function CitaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const citaId = id ? Number(id) : NaN;
  const base = useBase();
  const tz = useZonaHoraria();
  const { me } = useAuth();
  const [triajeOpen, setTriajeOpen] = useState(false);
  // Se llega aquí desde la bandeja de triaje con ?triaje=1: el formulario se
  // abre solo, que es lo que esperaba quien pulsó "Tomar triaje".
  const [params] = useSearchParams();
  const [abiertoPorUrl, setAbiertoPorUrl] = useState(false);
  const [derivarOpen, setDerivarOpen] = useState(false);
  const [agendarOpen, setAgendarOpen] = useState(false);

  const { data: cita, isLoading, error } = useQuery({
    queryKey: ["cita", citaId],
    queryFn: () => getCita(citaId),
    enabled: Number.isFinite(citaId),
  });

  // Triajes y derivaciones del paciente, filtrados a ESTA atención.
  const { data: triajes = [] } = useQuery({
    queryKey: ["triajes", cita?.paciente_id],
    queryFn: () => listTriajesDePaciente(cita!.paciente_id),
    enabled: cita != null,
  });
  const { data: asignaciones = [] } = useQuery({
    queryKey: ["asignaciones", cita?.paciente_id],
    queryFn: () => listAsignacionesDePaciente(cita!.paciente_id),
    enabled: cita != null,
  });
  const { data: citasPaciente = [] } = useQuery({
    queryKey: ["citas", "paciente", cita?.paciente_id],
    queryFn: () =>
      listCitas({ paciente_id: cita!.paciente_id, incluir_canceladas: true }),
    enabled: cita != null,
  });
  // Los tratamientos que nacieron de ESTA consulta. Sus sesiones también son
  // "lo que salió de aquí", igual que una derivación.
  const { data: planesDeLaAtencion = [] } = useQuery({
    queryKey: ["planes", "episodio", cita?.episodio_id],
    queryFn: () => listPlanes({ episodio_id: cita!.episodio_id! }),
    enabled: cita?.episodio_id != null,
  });

  if (isLoading) return <div className="text-sm text-slate-400">Cargando…</div>;
  if (error || !cita) return <Alert>No se pudo cargar la atención.</Alert>;

  const triaje = triajes.find((t) => t.cita_id === cita.id) ?? null;
  if (params.get("triaje") === "1" && !abiertoPorUrl && triaje == null && cita.pagada) {
    setAbiertoPorUrl(true);
    setTriajeOpen(true);
  }
  const derivaciones = asignaciones.filter((a) => a.cita_origen_id === cita.id);
  // Citas que salieron de esta atención: las que se agendaron contra una de
  // sus derivaciones. Así el control no queda huérfano en la lista general.
  const idsDerivacion = new Set(derivaciones.map((a) => a.id));
  // Los planes indicados en esta consulta. Sus sesiones salieron de aquí igual
  // que una derivación, y solo se miraba `asignacion_id`: por eso una atención
  // con dos controles agendados decía "Sin derivaciones" mientras la ficha del
  // paciente los enseñaba en Tratamientos. El dato estaba; faltaba mirarlo.
  const idsPlan = new Set(planesDeLaAtencion.map((p) => p.id));
  const relacionadas = citasPaciente.filter(
    (c) =>
      c.id !== cita.id &&
      ((c.asignacion_id != null && idsDerivacion.has(c.asignacion_id)) ||
        (c.plan_id != null && idsPlan.has(c.plan_id))),
  );
  // `pago:validar` es de caja. Recepción ve el estado pero no la acción.
  const puedeValidar =
    me?.is_super_admin || (me?.permisos.includes("pago:validar") ?? false);

  const ui = ESTADO[cita.estado] ?? { label: cita.estado, variant: "neutral" as const };
  const cerrada = cita.estado === "CANCELADA" || cita.estado === "NO_ASISTIO";
  const puedeCobrar = me?.is_super_admin || (me?.permisos.includes("pago:create") ?? false);
  const puedeAtender = me?.is_super_admin || (me?.permisos.includes("hce:write") ?? false);
  // El triaje lo toma enfermería, no el médico: el permiso salió del rol
  // médico a propósito (migración 0038). La pantalla no lo comprobaba y le
  // ofrecía "Tomar triaje" a quien el backend iba a rechazar.
  const puedeTriar = me?.is_super_admin || (me?.permisos.includes("triaje:create") ?? false);
  // No requerido no es prohibido: si no hace falta se puede tomar igual, pero
  // deja de bloquear la consulta.
  const triajeHecho = triaje != null;
  const triajeListo = triajeHecho || !cita.triaje_requerido;

  return (
    <div className="space-y-6">
      <Link
        to={`${base}/pacientes/${cita.paciente_id}`}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {cita.paciente_nombre}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">{cita.especialidad_nombre}</h1>
            <Badge variant={ui.variant}>{ui.label}</Badge>
            {/* Que recepción sepa de un vistazo que esta cita se la separó el
                paciente desde su móvil: cambia cómo se habla con él y explica
                por qué ya hay una constancia esperando. */}
            <EtiquetaOrigen origen={cita.origen} />
            {!cita.pagada && !cerrada && <Badge variant="warning">Por cobrar</Badge>}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            {fechaHora(cita.inicio, tz)} · {cita.profesional_nombre} · {cita.sede_nombre}
            {cita.servicio_nombre && ` · ${cita.servicio_nombre}`}
          </p>
          {cita.motivo && (
            <p className="mt-2 max-w-2xl rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {cita.motivo}
            </p>
          )}
          {/* Quién la registró. Una cita mal agendada la arregla quien la
              agendó, y hasta ahora no había forma de saber quién fue. */}
          <p className="mt-1 text-xs text-slate-400">
            Admisión: {cita.creado_por_nombre ?? "sin registrar"} ·{" "}
            {fechaHora(cita.created_at, tz)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-slate-500">Monto</p>
          <p className="font-mono text-2xl font-bold text-slate-900">
            S/ {cita.monto != null ? Number(cita.monto).toFixed(2) : "—"}
          </p>
        </div>
      </div>

      {/* La misma tira que ve el médico: cualquiera que abra esta atención
          —caja, triaje, recepción— sabe dónde está el paciente sin leer las
          cuatro tarjetas de abajo. */}
      <Card>
        <CardBody>
          <FlujoAtencionDeCita cita={cita} tieneTriaje={triaje != null} />
        </CardBody>
      </Card>

      {/* 1 — Pago. Va primero: sin pago no se atiende. */}
      <Paso
        n={1}
        icono={<Wallet className="h-4 w-4" />}
        titulo="Pago"
        hecho={cita.pagada}
        accion={
          !cita.pagada && !cerrada && puedeCobrar ? (
            <Link
              to={`${base}/caja?cita=${cita.id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--color-success)] px-4 py-2 text-sm font-medium text-white shadow-sm hover:brightness-110"
            >
              Cobrar en Caja
            </Link>
          ) : null
        }
      >
        {cita.pagada ? (
          /* Quién REGISTRÓ el pago y quién lo VALIDÓ no son la misma persona
             cuando viene de la app: lo registra el propio paciente al subir su
             captura y lo da por bueno caja. Decir solo "Cobrado por Katherine
             Salas" hacía parecer que la paciente se cobró a sí misma. */
          <p className="text-sm text-slate-700">
            {cita.pago_origen === "APP" ? (
              <>
                Lo pagó el paciente desde su app
                {cita.pago_fecha ? ` el ${fechaHora(cita.pago_fecha, tz)}` : ""}
                {cita.pago_validado_por_nombre
                  ? ` · confirmado por ${cita.pago_validado_por_nombre}`
                  : " · pendiente de confirmar en caja"}
                .
              </>
            ) : (
              <>
                Cobrado
                {cita.pago_registrado_por_nombre
                  ? ` por ${cita.pago_registrado_por_nombre}`
                  : ""}
                {cita.pago_fecha ? ` · ${fechaHora(cita.pago_fecha, tz)}` : "."}
              </>
            )}
          </p>
        ) : cita.pago_estado === "PENDIENTE_VALIDACION" ? (
          <p className="text-sm text-amber-700">
            {/* El enlace solo para quien puede validar. Quien no tiene el
                permiso —recepción— llegaba a Pagos y se encontraba con que no
                podía hacer nada: ofrecer una acción que va a fallar es peor
                que no ofrecerla. Lo que sí necesita es SABER que el pago está
                a la espera, para poder decírselo al paciente. */}
            {puedeValidar ? (
              <>
                Hay una constancia esperando validación.{" "}
                <Link to={`${base}/pagos`} className="font-medium underline">
                  Validarla
                </Link>
              </>
            ) : (
              <>
                El paciente ya pagó y subió su constancia. Caja tiene que
                revisarla para que la cita quede confirmada.
              </>
            )}
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            Pendiente. Sin el pago no se puede atender: es la única regla que el
            sistema impone.
          </p>
        )}
      </Paso>

      {/* 2 — Triaje */}
      <Paso
        n={2}
        icono={<Activity className="h-4 w-4" />}
        titulo="Triaje"
        hecho={triajeListo}
        accion={
          !triaje && !cerrada && cita.pagada && puedeTriar ? (
            <Button variant="ghost" onClick={() => setTriajeOpen(true)}>
              {cita.triaje_requerido ? "Tomar triaje" : "Tomarlo igualmente"}
            </Button>
          ) : null
        }
      >
        {triaje ? (
          <TriajeResumen triaje={triaje} fecha={fechaHora(triaje.fecha, tz)} />
        ) : !cita.pagada ? (
          <Bloqueado>Se habilita cuando la cita esté cobrada.</Bloqueado>
        ) : !cita.triaje_requerido ? (
          /* Se dice POR QUÉ no hace falta y no solo que no hace falta: sin el
             motivo, en la pantalla queda un paso saltado que parece un olvido
             y alguien acaba tomando vitales por si acaso. */
          <p className="text-sm text-slate-500">
            No hace falta para esta cita.{" "}
            {cita.triaje_motivo ?? ""}
            {puedeTriar && " Se puede tomar igual si el profesional lo pide."}
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            Pendiente. Lo toma enfermería antes de que el paciente pase con el
            profesional.
            {!puedeTriar && " Tú no necesitas hacer nada aquí."}
          </p>
        )}
      </Paso>

      {/* 3 — Consulta */}
      <Paso
        n={3}
        icono={<Stethoscope className="h-4 w-4" />}
        titulo="Consulta"
        hecho={cita.episodio_id != null}
        accion={
          cita.episodio_id ? (
            <Link
              to={`${base}/episodios/${cita.episodio_id}`}
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
            >
              Ver atención
            </Link>
          ) : cita.pagada && triajeListo && !cerrada && puedeAtender ? (
            <Link
              to={`${base}/pacientes/${cita.paciente_id}/atender?cita=${cita.id}`}
              className="inline-flex items-center gap-2 rounded-lg bg-[color:var(--color-link)] px-4 py-2 text-sm font-medium text-white hover:bg-[color:var(--color-link)]"
            >
              Atender
            </Link>
          ) : null
        }
      >
        {cita.episodio_id ? (
          <p className="text-sm text-slate-500">Atención registrada y firmada.</p>
        ) : !cita.pagada ? (
          <Bloqueado>Se habilita cuando la cita esté cobrada.</Bloqueado>
        ) : !triajeListo ? (
          <Bloqueado>
            Se habilita cuando le tomen el triaje. El médico no examina a alguien
            de quien no sabe ni la presión.
          </Bloqueado>
        ) : (
          <p className="text-sm text-slate-500">Lista para atender.</p>
        )}
      </Paso>

      {/* 4 — Desenlace */}
      <Paso
        n={4}
        icono={<FileSignature className="h-4 w-4" />}
        titulo="Derivación y control"
        hecho={derivaciones.length > 0 || relacionadas.length > 0}
        accion={
          !cerrada && cita.episodio_id != null ? (
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setDerivarOpen(true)}>
                Derivar
              </Button>
              <Button variant="ghost" onClick={() => setAgendarOpen(true)}>
                <CalendarPlus className="h-4 w-4" />
                Agendar control
              </Button>
            </div>
          ) : null
        }
      >
        {cita.episodio_id == null ? (
          <Bloqueado>
            Se habilita después de la consulta. Derivar es una decisión clínica:
            sin diagnóstico no hay a qué especialidad mandar al paciente ni por qué.
          </Bloqueado>
        ) : derivaciones.length === 0 && relacionadas.length === 0 ? (
          <p className="text-sm text-slate-500">
            Sin derivaciones. Deriva a otra especialidad o agenda un control si
            esta atención lo requiere.
          </p>
        ) : (
          <div className="space-y-3">
            {derivaciones.length > 0 && (
              <ul className="space-y-1 text-sm text-slate-700">
                {derivaciones.map((a) => (
                  <li key={a.id}>
                    → <strong>{a.especialidad_nombre}</strong>
                    {a.profesional_nombre
                      ? ` · ${a.profesional_nombre}`
                      : " · en cola del área"}
                    {a.motivo && <span className="text-slate-500"> — {a.motivo}</span>}
                  </li>
                ))}
              </ul>
            )}
            {relacionadas.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
                  Citas que salieron de esta atención
                </p>
                <ul className="space-y-1 text-sm">
                  {relacionadas.map((c) => (
                    <li key={c.id}>
                      <Link
                        to={`${base}/citas/${c.id}`}
                        className="text-[color:var(--color-link)] hover:underline"
                      >
                        {fechaHora(c.inicio, tz)} · {c.especialidad_nombre} ·{" "}
                        {c.profesional_nombre}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Paso>

      <TriajeDialog
        pacienteId={cita.paciente_id}
        cita={cita}
        open={triajeOpen}
        onClose={() => setTriajeOpen(false)}
      />
      <DerivarDialog
        pacienteId={cita.paciente_id}
        citaOrigen={cita}
        open={derivarOpen}
        onClose={() => setDerivarOpen(false)}
      />
      <AgendarCitaDialog
        pacienteId={cita.paciente_id}
        citaPrevia={cita}
        open={agendarOpen}
        onClose={() => setAgendarOpen(false)}
      />
    </div>
  );
}

/** Un paso que todavía no toca. Dice POR QUÉ, no solo que no se puede. */
function Bloqueado({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-sm text-slate-400">
      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

function Paso({
  n,
  icono,
  titulo,
  hecho,
  accion,
  children,
}: {
  /** Posición en el flujo. El orden de la pantalla es el orden real de trabajo. */
  n: number;
  icono: React.ReactNode;
  titulo: string;
  hecho: boolean;
  accion: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full ${
              hecho ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]" : "bg-slate-100 text-slate-400"
            }`}
          >
            {icono}
          </span>
          <h2 className="flex items-baseline gap-2 font-semibold text-slate-900">
            <span className="text-xs font-bold text-slate-400">{n}</span>
            {titulo}
          </h2>
          {hecho && <Badge variant="success">Hecho</Badge>}
          <div className="ml-auto">{accion}</div>
        </div>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}
